import React, { useState, useCallback } from 'react';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Button from '@amzn/meridian/button';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import {
  chainWalk,
  convertTimeStampToEpoch,
  isNumberInvalid,
  moveHardCodedElementsToBeginningOfArray,
  parseTimeStampToDateString
} from '../../utils/helpers';
import { DYNAMIC_PLAN_RENAMED_ROLES, FIRST_DYNAMIC_PLAN_ROLES_TO_SHOW, PLAN_STATUS } from '../../utils/constants';
import OverrideReason from '../commonComponents/OverrideReasons';
import processDynamicV1HeadCountTableData from '../../utils/processDynamicHeadCountTableData';

const UncontrolledExpandableRow = ({ children, rowComponents }) => {
  const [open, setOpen] = useState(false);
  const onClick = useCallback(() => setOpen(!open), [open]);
  return (
    <TableRow open={open} onClick={onClick} rowComponents={rowComponents} highlightOnHover={true}>
      {children}
    </TableRow>
  );
};

const EditableTableCell = ({ ind, value, setValue }) => {
  return (
    <TableCell key={ind}>
      <Textarea value={value} onChange={setValue} rows={1} />
    </TableCell>
  );
};

const DynamicShiftPlanMenu = ({ data, output, updatePlanStatus, updatePlanOutput }) => {
  try {
    output = JSON.parse(output);
  } catch (e) {
    return null;
  }

  // Set up visibility of override approve/update
  const isPlanPendingApproval = chainWalk(() => data['status'] === PLAN_STATUS.PENDING_APPROVAL, false);
  const isPlanUpdatable = chainWalk(() => data['status'] === PLAN_STATUS.PENDING_APPROVAL, false);

  // Relevant Data
  const times = chainWalk(() => processDynamicV1HeadCountTableData(output), []);
  // Roles to map for
  const roles = Object.keys(times).length > 0 ? Object.keys(times[Object.keys(times)[0]]) : [];

  // Header Timestamps
  const headerCells = Object.keys(times).map((val, ind) => (
    <TableCell key={ind}>{parseTimeStampToDateString(val)}</TableCell>
  ));

  // Get latest time that can be edited. Also get its index.
  let planCreationTime = data['createdAt'];
  let latestTimeStamp = isPlanUpdatable
    ? Object.keys(times).find((time) => planCreationTime < convertTimeStampToEpoch(time))
    : '';

  console.log(latestTimeStamp);
  const latestTimeStampIndex = latestTimeStamp ? Object.keys(times).indexOf(latestTimeStamp) : '';

  // Determine if time is editable
  const isTableCellEditable = (time) => {
    return latestTimeStamp && latestTimeStamp === time;
  };

  // Track changes for update output override
  let defaultLatestTimeStampData = {};
  let newLatestTimeStampData = {};

  // Determine if all headcount at role and time is zero
  const isPlannedHeadCountAtRoleAllZeroes = (headCountPlannerOutput, role) => {
    return Object.keys(headCountPlannerOutput).every((time) => headCountPlannerOutput[time][role] === 0);
  };

  // Move roles in FIRST_DYNAMIC_ROLES_TO_SHOW at the beginning, then show rest of roles.
  const reOrderedRoles = roles.reduce(
    () => moveHardCodedElementsToBeginningOfArray(FIRST_DYNAMIC_PLAN_ROLES_TO_SHOW, roles),
    []
  );

  // Remove roles that have all zeroes for headcount for entire shift.
  const filteredRoles = reOrderedRoles.filter((role) => !isPlannedHeadCountAtRoleAllZeroes(times, role)); //.

  // Update role name display if in DYNAMIC_PLAN_RENAMED_ROLES
  const getUpdatedRoleName = (role) => {
    return role in DYNAMIC_PLAN_RENAMED_ROLES ? DYNAMIC_PLAN_RENAMED_ROLES[role] : role;
  };

  // Row JSX
  const rows = filteredRoles.map((role, ind) => {
    return (
      <UncontrolledExpandableRow key={ind}>
        <TableCell width="30vh">{getUpdatedRoleName(role)}</TableCell>
        {Object.keys(times).map((time, ind) => {
          // Getting all the data at a particular timestamp
          const actualCount = times[time][role];
          // TableCell if cell isn't updatable.
          if (!isTableCellEditable(time)) {
            return <TableCell key={ind}>{actualCount}</TableCell>;
          }
          // Set up EditableTableCell. Also keep track of values.
          const [value, setValue] = useState(actualCount);
          defaultLatestTimeStampData[role] = actualCount;
          newLatestTimeStampData[role] = actualCount;
          return <EditableTableCell key={ind} ind={ind} value={value} setValue={setValue} />;
        })}
      </UncontrolledExpandableRow>
    );
  });

  // Read data from table and check if any data changes.
  const hasLatestTimeStampDataChanged = () => {
    for (let i in rows) {
      let row = rows[i];
      // Rows and filteredRoles have same index based on how the table was populated.
      let role = filteredRoles[i];
      let headCount = chainWalk(() => row['props']['children'][1][latestTimeStampIndex]['props']['value'], '');
      newLatestTimeStampData[role] = !isNumberInvalid(headCount) || headCount === '0' ? parseInt(headCount) : '';
    }

    return !(JSON.stringify(defaultLatestTimeStampData) === JSON.stringify(newLatestTimeStampData));
  };

  // Validate override text
  const isDataValid = (dataset) => {
    if (Object.keys(dataset).length === 0) {
      return false;
    }
    for (let i in dataset) {
      let roleData = dataset[i];
      if (isNumberInvalid(roleData)) {
        return false;
      }
    }
    return true;
  };

  // Update Plan
  const onUpdate = () => {
    if (!isDataValid(newLatestTimeStampData)) {
      console.log('Error output');
      return;
    }
    updatePlanOutput(
      data['planId'],
      defaultLatestTimeStampData,
      newLatestTimeStampData,
      latestTimeStamp,
      overrideReason,
      overrideReasonComment
    );
  };

  // Approve Plan
  const onApprove = () => {
    updatePlanStatus(PLAN_STATUS.APPROVED, data['planId']);
  };

  const isApproveDisabled = latestTimeStamp && hasLatestTimeStampDataChanged();
  const isUpdateDisabled = !latestTimeStamp || !hasLatestTimeStampDataChanged();
  const isOverrideReasonDisabled = !latestTimeStamp || !hasLatestTimeStampDataChanged();

  const [overrideReason, setOverrideReason] = useState('');
  const overrideOptions = ['Invalid Headcount', 'Other'];
  const overrideExceptions = ['Other'];
  const [overrideReasonComment, setOverrideReasonComment] = useState('');

  return (
    <div>
      <div style={{ marginBottom: '3vh', overflowX: 'auto' }}>
        <Table
          headerRows={1}
          headerColumns={1}
          showDividers={true}
          spacing="small"
          showStripes={true}
          fixHeaderRows={true}
          fixHeaderRowsFullWidth={true}
          rowComponents={[UncontrolledExpandableRow, TableRow]}>
          <TableRow>
            <TableCell />
            {headerCells}
          </TableRow>
          {rows}
        </Table>
      </div>
      <div>
        {isPlanPendingApproval ? (
          <div style={{ marginBottom: '5vh' }}>
            <Row>
              <Box>
                <Text type="h200">Override Options:</Text>
              </Box>
              <Box width="15vh">
                <Button onClick={onApprove} disabled={isApproveDisabled}>
                  Approve
                </Button>
              </Box>
              {isPlanUpdatable ? (
                <Box>
                  <Button onClick={onUpdate} disabled={isUpdateDisabled}>
                    Update
                  </Button>
                </Box>
              ) : null}
            </Row>
          </div>
        ) : null}
        {isPlanUpdatable ? (
          <div>
            <OverrideReason
              overrideReason={overrideReason}
              setOverrideReason={setOverrideReason}
              overrideReasonComment={overrideReasonComment}
              setOverrideReasonComment={setOverrideReasonComment}
              options={overrideOptions}
              exceptions={overrideExceptions}
              disable={isOverrideReasonDisabled}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DynamicShiftPlanMenu;
